/**
 * Decision 0146 (memory phase 2, L5): what an agent tending its memory looks at. `memory_review` answers it; the agent
 * decides what to change and changes one claim per call (`remember` with supersedes, replaces, forget or confirm):
 * itemized deltas, never a wholesale rewrite (ACE's context collapse). Pure; similarity comes from the caller
 * (cosine of the bge-m3 vectors when the meaning index is on, else shared words).
 *
 * The tending wake is a loose end ({@link memoryTendingDue}), like briefs: the daily check names it when enough memory
 * changed since the last one, and the agent judges whether a pass is worth it.
 */

import type { MemoryItem, ProjectState } from "../types"
import { memoryChecked } from "./memory-refs"
import { isCurrentMemory } from "./memory-validity"
import { MEMORY_SIMILAR_MIN } from "./recall"

const DAY = 86_400_000
/** Two current claims this close are likely the same claim. */
export const MEMORY_DUPLICATE_MIN = 0.85
/** A fact or learning not revised or confirmed for this long is listed as stale. */
export const MEMORY_STALE_MS = 90 * DAY
/** Every this many memory changes (writes, revisions, supersessions, forgets) the lead's tending is due again. */
export const MEMORY_TEND_EVERY = 10
/** Tending is not offered below this many current claims. */
export const MEMORY_TEND_MIN_CLAIMS = 5

export interface ReviewPair {
  /** The older claim's ref, then the newer one's. */
  readonly older: string
  readonly newer: string
  readonly similarity: number
  readonly suggestion: string
}

export interface ReviewLine {
  readonly ref: string
  readonly title: string
  readonly note: string
}

export interface MemoryReview {
  readonly current: number
  readonly duplicates: ReadonlyArray<ReviewPair>
  readonly close: ReadonlyArray<ReviewPair>
  readonly stale: ReadonlyArray<ReviewLine>
  readonly due: ReadonlyArray<ReviewLine>
  readonly ended: ReadonlyArray<ReviewLine>
  /** Memory changes agents made in the last 7 days (what a person or the lead may want to look at, and undo). */
  readonly recent: ReadonlyArray<ReviewLine>
}

const day = (t: number): string => new Date(t).toISOString().slice(0, 10)

const WORD = /[\p{L}\p{N}]{4,}/gu
const wordsOf = (m: MemoryItem): ReadonlySet<string> =>
  new Set(
    `${m.title} ${m.content._tag === "fact" ? m.content.summary : ""}`
      .toLowerCase()
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .match(WORD) ?? [],
  )

/** Shared words over the smaller set (the fallback similarity without vectors). */
export const wordSimilarity = (a: MemoryItem, b: MemoryItem): number => {
  const x = wordsOf(a)
  const y = wordsOf(b)
  if (x.size === 0 || y.size === 0) return 0
  let n = 0
  for (const w of x) if (y.has(w)) n++
  return n / Math.min(x.size, y.size)
}

const lastTouched = (m: MemoryItem): number => Math.max(m.updatedAt, m.lastConfirmedAt ?? 0)

/**
 * The review of a project's (or one area's) current memory. `similarity(a, b)` in [0, 1], null when unknown (then
 * shared words). Pairs are listed best first, at most `limit` per list.
 */
export const memoryReview = (
  s: ProjectState,
  now: number,
  opts: {
    readonly similarity?: (a: MemoryItem, b: MemoryItem) => number | null
    /** Only this area's memory (an area lead); null/absent: the project's and every area's. */
    readonly areaId?: string | null
    readonly limit?: number
  } = {},
): MemoryReview => {
  const limit = opts.limit ?? 20
  const mine = (m: MemoryItem) =>
    (m.scope._tag === "project" || m.scope._tag === "area") &&
    (opts.areaId == null || (m.scope._tag === "area" && m.scope.areaId === opts.areaId))
  const current = s.memory.filter((m) => mine(m) && isCurrentMemory(m, now) && m.content._tag !== "journal")
  const ref = (m: MemoryItem) => m.ref ?? m.id
  const pairs: Array<ReviewPair> = []
  for (let i = 0; i < current.length; i++)
    for (let j = i + 1; j < current.length; j++) {
      const a = current[i] as MemoryItem
      const b = current[j] as MemoryItem
      const sim = opts.similarity?.(a, b) ?? wordSimilarity(a, b)
      if (sim < MEMORY_SIMILAR_MIN) continue
      const [older, newer] = a.createdAt <= b.createdAt ? [a, b] : [b, a]
      const keep = memoryChecked(older) && !memoryChecked(newer) ? older : newer
      const drop = keep === older ? newer : older
      const suggestion =
        sim >= MEMORY_DUPLICATE_MIN
          ? `Likely the same claim: keep ${ref(keep)}${memoryChecked(keep) ? " (a person wrote or checked it)" : ""}; forget ${ref(drop)}, or supersede it with one merged claim.`
          : `Same subject: if ${ref(newer)} updates ${ref(older)}, remember {supersedes: "${ref(older)}"} (the old stays as history); if they disagree, check the record and confirm the one that holds; if both hold, leave them.`
      pairs.push({
        older: ref(older),
        newer: ref(newer),
        similarity: Math.round(sim * 100) / 100,
        suggestion,
      })
    }
  pairs.sort((x, y) => y.similarity - x.similarity || (x.older < y.older ? -1 : 1))
  const stale = current
    .filter(
      (m) =>
        (m.content._tag === "fact" || m.content._tag === "learning") &&
        now - lastTouched(m) >= MEMORY_STALE_MS,
    )
    .sort((a, b) => lastTouched(a) - lastTouched(b))
    .map((m) => ({
      ref: ref(m),
      title: m.title,
      note: `not revised or confirmed since ${day(lastTouched(m))}: check it against the record, then confirm, supersede or forget it`,
    }))
  const due = current
    .filter((m) => m.content._tag === "learning" && m.content.revisitAt <= now)
    .map((m) => ({
      ref: ref(m),
      title: m.title,
      note: `due for a re-check since ${day(m.content._tag === "learning" ? m.content.revisitAt : now)}`,
    }))
  const ended = s.memory
    .filter(
      (m) =>
        mine(m) &&
        m.status === "accepted" &&
        m.supersededBy === undefined &&
        m.validTo !== undefined &&
        m.validTo <= now,
    )
    .map((m) => ({
      ref: ref(m),
      title: m.title,
      note: `ended ${day(m.validTo ?? now)}: it no longer loads; supersede it if something replaced it`,
    }))
  const recent = s.memory
    .filter((m) => mine(m) && m.updatedBy._tag !== "user" && now - m.updatedAt <= 7 * DAY)
    .sort((a, b) => b.updatedAt - a.updatedAt)
    .map((m) => ({
      ref: ref(m),
      title: m.title,
      note:
        m.status !== "accepted"
          ? `forgotten ${day(m.updatedAt)} (Restore brings it back)`
          : m.supersededBy
            ? `superseded ${day(m.updatedAt)} (forgetting its successor brings it back)`
            : `${m.revision > 1 ? "revised" : "written"} ${day(m.updatedAt)}`,
    }))
  return {
    current: current.length,
    duplicates: pairs.filter((p) => p.similarity >= MEMORY_DUPLICATE_MIN).slice(0, limit),
    close: pairs.filter((p) => p.similarity < MEMORY_DUPLICATE_MIN).slice(0, limit),
    stale: stale.slice(0, limit),
    due: due.slice(0, limit),
    ended: ended.slice(0, limit),
    recent: recent.slice(0, limit),
  }
}

/** How many times the project's memory changed: every write and revision counts (Σ revisions), and every forget. */
export const memoryActivity = (s: ProjectState): number =>
  s.memory.reduce(
    (n, m) =>
      m.scope._tag === "project" || m.scope._tag === "area"
        ? n + m.revision + (m.status === "accepted" ? 0 : 1)
        : n,
    0,
  )

/**
 * Decision 0146: the lead's memory tending is due every {@link MEMORY_TEND_EVERY} memory changes, once the project
 * holds at least {@link MEMORY_TEND_MIN_CLAIMS} current claims. The key is the step, so it is noted once per step; the
 * lead decides whether a pass is worth it (agent judgement, no fixed cadence).
 */
export const memoryTendingDue = (
  s: ProjectState,
  now: number,
): { readonly key: string; readonly line: string } | null => {
  const current = s.memory.filter(
    (m) => (m.scope._tag === "project" || m.scope._tag === "area") && isCurrentMemory(m, now),
  ).length
  if (current < MEMORY_TEND_MIN_CLAIMS) return null
  const step = Math.floor(memoryActivity(s) / MEMORY_TEND_EVERY)
  if (step === 0) return null
  return {
    key: `tend:${step}`,
    line: `Memory has changed ${step * MEMORY_TEND_EVERY}+ times (${current} current claims): a quiet tending pass may be due. \`memory_review\` lists likely duplicates, claims on the same subject, stale ones and ended ones; change one claim per call (remember with supersedes, replaces, forget or confirm), never rewrite memory wholesale. Skip it when nothing needs care.`,
  }
}
